import { Injectable } from '@angular/core';
import { merge } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, skip, take } from 'rxjs/operators';

const EXPIRATION_TIME = 3600000;

interface IRequestResult {
  expiresAt: number;
  data: any;
  error: any;
}

/**
 * API cache service.
 *
 * Responses for all GET requests will be cached by url in memory and used for other calls
 * until it expries or is forced to fetch data from server again.
 * Cache expiration time is 1 hour.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiCacheService {
  private _requests: Map<string, BehaviorSubject<IRequestResult>> = new Map();
  private _loading: Map<string, boolean> = new Map();
  private _aliases: Map<string, string[]> = new Map();

  constructor() {}

  /**
   * Sets loading status of a request.
   *
   * This is used to wait until the first request resolves data.
   *
   * @param key Request key. Generally url of the request.
   * @param loading Loading status
   * @param alias Alias keys of the request
   */
  public setLoading(key: string, loading: boolean, alias?: string[]) {
    this._loading.set(key, loading);
    if (!this._requests.has(key)) {
      this._requests.set(
        key,
        new BehaviorSubject({
          expiresAt: 0,
          data: null,
          error: null,
        })
      );
    }
    if (alias) {
      alias.forEach((alias) => {
        if (!this._aliases.get(alias)) {
          this._aliases.set(alias, [key]);
        } else {
          const keys = this._aliases.get(alias);
          if (!keys.includes(key)) {
            this._aliases.set(alias, [...keys, key]);
          }
        }
      });
    }
  }

  /**
   * Get cache data
   * @param key Request key. Generally url of the request.
   * @returns Response data
   */
  public getCache(key: string): Observable<any> {
    if (this._loading.get(key)) {
      return this._requests.get(key).pipe(
        skip(1),
        take(1),
        map((value: IRequestResult) => value)
      );
    } else {
      const request = this._requests.get(key);
      if (request?.value) {
        const ts = new Date().getTime();
        if (request.value.expiresAt > ts) {
          return of(request.value);
        }
      }
    }

    return of({});
  }

  /**
   * Store response data to cache
   * @param key Request key. Generally url of the request.
   * @param val Response data of the request
   * @param error Error object if the request failed
   * @param alias Alias keys of the request
   */
  public setCache(key: string, val: any, error: any, alias?: string[]) {
    this._requests.get(key).next({
      expiresAt: new Date().getTime() + EXPIRATION_TIME,
      data: val,
      error,
    });
    this.setLoading(key, false, alias);
  }

  /**
   * Update cache data.
   *
   * This method does not overwrite the data. It merges data to existing one.
   *
   * @param key Cache key
   * @param val Update value
   */
  public updateCache(key: string, val: any) {
    if (this._requests.has(key)) {
      const { error, data } = this._requests.get(key).value;
      this._requests.get(key).next({
        expiresAt: new Date().getTime() + EXPIRATION_TIME,
        data: typeof val === 'object' ? merge(data, val) : val,
        error,
      });
    }
  }

  /**
   * Clear all cache
   */
  public clear() {
    this._requests.clear();
    this._loading.clear();
    this._aliases.clear();
  }

  /**
   * Invalidate cache data
   * @param key Request Key.
   */
  public invalidate(key: string) {
    if (this._aliases.has(key)) {
      this._aliases.get(key).forEach((url) => {
        this._requests.delete(url);
      });
    }

    this._requests.delete(key);
    this._aliases.delete(key);
  }
}
